/**
 * TaskDetailsSheetContent Component
 *
 * Card-based content for the mobile bottom sheet.
 * Displays task details in styled cards matching the drawer layout pattern.
 *
 * Cards:
 * - Details (status, urgency, dates, metadata)
 * - Assigned To (assignee display + collaborators)
 * - Followers (overlapping avatar stack)
 */

import React from 'react';
import PropTypes from 'prop-types';
import { Button, DatePicker, Select, Tooltip, message, Tag } from 'antd';
import {
  CalendarOutlined,
  CheckOutlined,
  ClockCircleOutlined,
  CloseOutlined,
  EditOutlined,
  InfoCircleOutlined,
  LinkOutlined,
  MinusOutlined,
  PlusOutlined,
  UserOutlined,
  SyncOutlined,
} from '@ant-design/icons';
import { startCase } from 'lodash';
import AvatarS3 from '../../../../shared/images/AvatarS3';
import {
  urgencyColors,
  urgencyTypes,
  taskTypes,
  subTypes,
} from '../../configs/task.config';
import { neutral, status as statusTokens } from '../../configs/designTokens';
import { assigneeOptions, isDirectTask } from '../../utils/task.utils';
import RequestTypeDetailsView from '../shared/RequestTypeDetailsView';
import '../../styles/index.scss';

const { Option } = Select;

/**
 * Maps a task status string like 'IN_PROGRESS' to a camelCase key
 * and returns the corresponding color token.
 */
const getStatusColor = (taskStatus) => {
  if (!taskStatus) return neutral[400];
  const key = taskStatus
    .toLowerCase()
    .replace(/_([a-z])/g, (_, c) => c.toUpperCase());
  return statusTokens[key] || neutral[400];
};

const TaskDetailsSheetContent = ({
  currentTask,
  currentUser,
  currentTaskId,
  isFollowers,
  isCurrentUserAssignee,
  isCollaborator,
  updateTaskLoading,
  updateTask,
  refetchTask,
  onFollowChange,
  onCollaboratorsUpdate,
  level,
  // Inline assignee editing
  isEditingAssignee,
  setIsEditingAssignee,
  selectedOrg,
  setSelectedOrg,
  selectedAssignees,
  setSelectedAssignees,
  fetchBasicUsers,
  basicOrgsDetailsLoading,
  basicUsersDetails,
  basicUsersDetailsLoading,
  onUserUpdate,
  teams,
  teamsLoading,
  selectedTeamId,
  setSelectedTeamId,
  fetchTeamsByOrg,
  // Inline details editing
  isEditingDetails,
  setIsEditingDetails,
  detailsForm,
  onDetailsFieldChange,
  onDetailsSave,
  onDetailsCancel,
  channelPartnerOrgs,
  advertiserOrgs,
  fetchChannelPartnerOrgs,
  fetchAdvertiserOrgs,
}) => {
  if (!currentTask) return null;

  const availableSubTypes = detailsForm && detailsForm.requestType
    ? subTypes[detailsForm.requestType] || []
    : [];

  // Mirrors the rules in TaskBasicFields: due date required only when not linked to
  // a project; start must be strictly before due when both are set.
  const isDueDateRequired = !currentTask.project;
  const isDueDateMissing = isDueDateRequired && !(detailsForm && detailsForm.dueDate);
  const isDetailsSaveDisabled =
    updateTaskLoading ||
    !(detailsForm && detailsForm.channelPartnerId) ||
    !(detailsForm && detailsForm.advertiserId) ||
    isDueDateMissing;

  const onEditDetailsClick = () => {
    setIsEditingDetails(true);
    fetchChannelPartnerOrgs();
    if (currentTask.channelPartner && currentTask.channelPartner.id) {
      fetchAdvertiserOrgs('', currentTask.channelPartner.id);
    }
  };

  const assignee = currentTask.assigneeUsers?.[0];
  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const handleExternalLink = () => {
    const url = currentTask?.linkUrl;
    if (url && (url.startsWith('http://') || url.startsWith('https://'))) {
      window.open(url, '_blank', 'noopener,noreferrer');
    }
  };

  return (
    <div className="task-sheet-content">
      {/* ── Details Card ── */}
      <div className="task-sheet-content__card">
        <div className="task-sheet-content__card-header">
          <div className="task-sheet-content__card-title">
            <ClockCircleOutlined className="task-sheet-content__card-icon" />
            <span>Details</span>
          </div>
          <div className="task-sheet-content__card-header-actions">
            {currentTask.linkUrl && (
              <Button
                type="text"
                size="small"
                onClick={handleExternalLink}
                className="task-sheet-content__link-btn"
              >
                <LinkOutlined /> Link
              </Button>
            )}
            {!isEditingDetails && level === 'PLATFORM' && (
              <Button
                type="text"
                size="small"
                className="task-sheet-content__action-btn"
                onClick={onEditDetailsClick}
              >
                <EditOutlined /> Edit
              </Button>
            )}
          </div>
        </div>

        <div className="task-sheet-content__details-grid">
          {/* Status (read-only — has its own workflow) */}
          <div className="task-sheet-content__detail">
            <span className="task-sheet-content__detail-label">Status</span>
            <span className="task-sheet-content__detail-value">
              <span
                className="task-sheet-content__status-dot"
                style={{ backgroundColor: getStatusColor(currentTask.status) }}
              />
              {startCase(currentTask.status?.toLowerCase().replace(/_/g, ' ') || 'Unknown')}
            </span>
          </div>

          {/* Urgency */}
          <div className="task-sheet-content__detail">
            <span className="task-sheet-content__detail-label">Urgency</span>
            {isEditingDetails ? (
              <Select
                size="small"
                style={{ width: '100%' }}
                value={detailsForm.urgency || undefined}
                placeholder="Select Urgency"
                allowClear
                onChange={(value) => onDetailsFieldChange('urgency', value || null)}
              >
                {Object.entries(urgencyTypes).map(([label, value]) => (
                  <Option key={value} value={value}>
                    {label}
                  </Option>
                ))}
              </Select>
            ) : (
              <span className="task-sheet-content__detail-value">
                {currentTask.urgency ? (
                  <>
                    <span
                      className="task-sheet-content__status-dot"
                      style={{ backgroundColor: urgencyColors[currentTask.urgency] }}
                    />
                    {startCase(currentTask.urgency.toLowerCase())}
                  </>
                ) : (
                  '-'
                )}
              </span>
            )}
          </div>

          {/* Due Date (required when not linked to a project) */}
          <div className="task-sheet-content__detail">
            <span className="task-sheet-content__detail-label">
              Due Date
              {isEditingDetails && isDueDateRequired && (
                <span style={{ color: '#ff4d4f' }}> *</span>
              )}
            </span>
            {isEditingDetails ? (
              <DatePicker
                size="small"
                style={{ width: '100%' }}
                value={detailsForm.dueDate}
                onChange={(value) => onDetailsFieldChange('dueDate', value)}
              />
            ) : (
              <span className="task-sheet-content__detail-value">
                <CalendarOutlined />
                {formatDate(currentTask.dueDate)}
                {currentTask.recurring && (
                  <SyncOutlined className="task-sheet-content__recurring" />
                )}
              </span>
            )}
          </div>

          {/* Start Date */}
          <div className="task-sheet-content__detail">
            <span className="task-sheet-content__detail-label">Start Date</span>
            {isEditingDetails ? (
              <DatePicker
                size="small"
                style={{ width: '100%' }}
                value={detailsForm.startDate}
                onChange={(value) => onDetailsFieldChange('startDate', value)}
              />
            ) : (
              <span className="task-sheet-content__detail-value">
                {formatDate(currentTask.startDate)}
              </span>
            )}
          </div>

          {/* Created (read-only) */}
          <div className="task-sheet-content__detail">
            <span className="task-sheet-content__detail-label">Created</span>
            <span className="task-sheet-content__detail-value">
              {formatDate(currentTask.createdAt)}
            </span>
          </div>

          {/* Requested By (read-only) */}
          <div className="task-sheet-content__detail">
            <span className="task-sheet-content__detail-label">Requested By</span>
            <span className="task-sheet-content__detail-value">
              {startCase(currentTask.createdBy?.name || currentTask.createdBy?.username || 'Unknown')}
            </span>
          </div>

          {/* Channel Partner (required) */}
          {(isEditingDetails || currentTask.channelPartner) && (
            <div className="task-sheet-content__detail">
              <span className="task-sheet-content__detail-label">
                Channel Partner
                {isEditingDetails && <span style={{ color: '#ff4d4f' }}> *</span>}
              </span>
              {isEditingDetails ? (
                <Select
                  size="small"
                  style={{ width: '100%' }}
                  value={detailsForm.channelPartnerId || undefined}
                  placeholder="Select Channel Partner"
                  showSearch
                  loading={basicOrgsDetailsLoading}
                  onFocus={() => fetchChannelPartnerOrgs()}
                  onSearch={(value) => fetchChannelPartnerOrgs(value)}
                  onChange={(value) => {
                    onDetailsFieldChange('channelPartnerId', value || null);
                    if (value) fetchAdvertiserOrgs('', value);
                  }}
                  filterOption={(input, option) =>
                    option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                  }
                >
                  {[...channelPartnerOrgs]
                    .sort((a, b) => (a.name || '').localeCompare(b.name || ''))
                    .map((org) => (
                      <Option key={org.id} value={org.id}>
                        {org.name}
                      </Option>
                    ))}
                </Select>
              ) : (
                <span className="task-sheet-content__detail-value">
                  {currentTask.channelPartner.name}
                </span>
              )}
            </div>
          )}

          {/* Advertiser (required) */}
          {(isEditingDetails || currentTask.advertiser) && (
            <div className="task-sheet-content__detail">
              <span className="task-sheet-content__detail-label">
                Advertiser
                {isEditingDetails && <span style={{ color: '#ff4d4f' }}> *</span>}
              </span>
              {isEditingDetails ? (
                <Select
                  size="small"
                  style={{ width: '100%' }}
                  value={detailsForm.advertiserId || undefined}
                  placeholder={
                    detailsForm.channelPartnerId
                      ? 'Select Advertiser'
                      : 'Select channel partner first'
                  }
                  showSearch
                  disabled={!detailsForm.channelPartnerId}
                  loading={basicOrgsDetailsLoading}
                  onFocus={() => fetchAdvertiserOrgs()}
                  onSearch={(value) => fetchAdvertiserOrgs(value)}
                  onChange={(value) => onDetailsFieldChange('advertiserId', value || null)}
                  filterOption={(input, option) =>
                    option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                  }
                >
                  {[...advertiserOrgs]
                    .sort((a, b) => (a.name || '').localeCompare(b.name || ''))
                    .map((org) => (
                      <Option key={org.id} value={org.id}>
                        {org.name}
                      </Option>
                    ))}
                </Select>
              ) : (
                <span className="task-sheet-content__detail-value">
                  {currentTask.advertiser.name}
                  {isDirectTask(currentTask) && (
                    <Tag color="green" style={{ marginLeft: 6 }}>
                      Direct
                    </Tag>
                  )}
                </span>
              )}
            </div>
          )}

          {/* Request Type + Sub Type */}
          {isEditingDetails ? (
            <>
              <div className="task-sheet-content__detail task-sheet-content__detail--full">
                <span className="task-sheet-content__detail-label">Request Type</span>
                <Select
                  size="small"
                  style={{ width: '100%' }}
                  value={detailsForm.requestType || undefined}
                  placeholder="Select Request Type"
                  allowClear
                  showSearch
                  onChange={(value) => onDetailsFieldChange('requestType', value || null)}
                  filterOption={(input, option) =>
                    option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                  }
                >
                  {taskTypes.map((type) => (
                    <Option key={type} value={type}>
                      {type}
                    </Option>
                  ))}
                </Select>
              </div>
              <div className="task-sheet-content__detail task-sheet-content__detail--full">
                <span className="task-sheet-content__detail-label">Request Details</span>
                <Select
                  size="small"
                  style={{ width: '100%' }}
                  value={detailsForm.requestSubType || undefined}
                  placeholder={
                    availableSubTypes.length > 0
                      ? 'Select Sub Type'
                      : 'No sub-types for this request type'
                  }
                  allowClear
                  disabled={availableSubTypes.length === 0}
                  onChange={(value) => onDetailsFieldChange('requestSubType', value || null)}
                >
                  {availableSubTypes.map((sub) => (
                    <Option key={sub} value={sub}>
                      {sub}
                    </Option>
                  ))}
                </Select>
              </div>
            </>
          ) : (
            currentTask.requestType && (
              <div className="task-sheet-content__detail task-sheet-content__detail--full">
                <span className="task-sheet-content__detail-label">Request Type</span>
                <span className="task-sheet-content__detail-value">
                  {currentTask.requestType}
                  {currentTask.requestSubType && ` → ${currentTask.requestSubType}`}
                </span>
              </div>
            )
          )}
        </div>

        {!isEditingDetails && <RequestTypeDetailsView task={currentTask} />}

        {isEditingDetails && (
          <div className="task-sheet-content__edit-actions">
            <Button
              size="small"
              icon={<CloseOutlined />}
              onClick={onDetailsCancel}
              disabled={updateTaskLoading}
            >
              Cancel
            </Button>
            <Button
              type="primary"
              size="small"
              icon={<CheckOutlined />}
              loading={updateTaskLoading}
              onClick={onDetailsSave}
              disabled={isDetailsSaveDisabled}
            >
              Save
            </Button>
          </div>
        )}
      </div>

      {/* ── Assignees Card ── */}
      <div className="task-sheet-content__card">
        <div className="task-sheet-content__card-header">
          <div className="task-sheet-content__card-title">
            <UserOutlined className="task-sheet-content__card-icon task-sheet-content__card-icon--success" />
            <span>Assigned To</span>
          </div>
          {!isEditingAssignee && level === 'PLATFORM' && (
            <Button
              type="text"
              size="small"
              className="task-sheet-content__action-btn"
              onClick={() => {
                setIsEditingAssignee(true);
                // Org isn't picked here anymore — load teams for the task's org (fetchTeamsByOrg
                // falls back to the current user's org) so the Team picker is ready.
                fetchTeamsByOrg(currentTask?.assigneeOrg?.id);
              }}
            >
              <EditOutlined /> Edit
            </Button>
          )}
        </div>

        {/* Assignee Display (non-editing) */}
        {!isEditingAssignee && assignee && (
          <div className="task-sheet-content__assignee-display">
            <AvatarS3
              imgKey={assignee.avatar?.key}
              username={assignee.name || assignee.username}
              avatarProps={{ size: 32 }}
            />
            <div className="task-sheet-content__assignee-info">
              <div className="task-sheet-content__assignee-name">
                {startCase(assignee.name || assignee.username)}
              </div>
              {currentTask.assigneeOrg?.name && (
                <div className="task-sheet-content__assignee-org">
                  {currentTask.assigneeOrg.name}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Unassigned (non-editing) */}
        {!isEditingAssignee && !assignee && (
          <p className="task-sheet-content__empty-text">
            No one assigned yet.
          </p>
        )}

        {/* Editing Mode */}
        {isEditingAssignee && (
          <div className="task-sheet-content__assignment">
            {/* Organization is no longer selected here — the assignable users/teams are scoped to
                the current user's org (fetchTeamsByOrg / fetchBasicUsers fall back to it), matching
                the drawer + full-page details. Team and Assignee are both optional. */}
            {/* Team — hidden for project-linked tasks (assigned via the project, not a team) */}
            {!currentTask?.project && (
              <div className="task-sheet-content__detail">
                <span className="task-sheet-content__detail-label">
                  Team
                  <Tooltip title="Changing the team adds the new team's subscribers as collaborators and keeps everyone already on this task — existing collaborators are not removed.">
                    <InfoCircleOutlined style={{ marginLeft: 6, color: '#faad14', cursor: 'help' }} />
                  </Tooltip>
                </span>
                <Select
                  placeholder="Select Team (optional)"
                  showSearch
                  allowClear
                  loading={teamsLoading}
                  value={selectedTeamId}
                  onFocus={() => fetchTeamsByOrg(selectedOrg)}
                  onSearch={(searchText) => fetchTeamsByOrg(selectedOrg, searchText)}
                  onChange={(value) => {
                    setSelectedTeamId(value || null);
                    setSelectedAssignees([]);
                  }}
                  filterOption={false}
                  style={{ width: '100%' }}
                  size="small"
                >
                  {(teams || []).map((team) => (
                    <Option key={team.id} value={team.id}>
                      {team.name}
                    </Option>
                  ))}
                </Select>
              </div>
            )}

            <div className="task-sheet-content__detail">
              <span className="task-sheet-content__detail-label">
                Assignee
                {currentTask.project && (
                  <span className="task-sheet-content__detail-hint">(from project)</span>
                )}
              </span>
              {!currentTask.project ? (
                <Select
                  placeholder="Select Assignee"
                  loading={basicUsersDetailsLoading}
                  showSearch
                  filterOption={(input, option) =>
                    option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                  }
                  onFocus={() => fetchBasicUsers()}
                  onSearch={(value) => fetchBasicUsers(value)}
                  value={selectedAssignees}
                  onChange={(value) => setSelectedAssignees(value)}
                  style={{ width: '100%' }}
                  size="small"
                >
                  {(() => {
                    const filteredUsers = selectedTeamId
                      ? (() => {
                          const selectedTeam = (teams || []).find((t) => t.id === selectedTeamId);
                          if (!selectedTeam?.members) return basicUsersDetails;
                          const memberIds = selectedTeam.members.map((m) => m.id);
                          return basicUsersDetails.filter((u) => memberIds.includes(u.id));
                        })()
                      : basicUsersDetails;
                    return assigneeOptions(filteredUsers, currentTask.assigneeUsers).map((user) => (
                      <Option key={user.id} value={user.id}>
                        {user.name || user.username}
                      </Option>
                    ));
                  })()}
                </Select>
              ) : (
                <span className="task-sheet-content__detail-value">
                  {currentTask.assigneeUsers?.length > 0
                    ? currentTask.assigneeUsers.map((u) => startCase(u.name || u.username)).join(', ')
                    : '-'}
                </span>
              )}
            </div>

            <div className="task-sheet-content__edit-actions">
              <Button
                size="small"
                icon={<CloseOutlined />}
                onClick={() => {
                  setIsEditingAssignee(false);
                  setSelectedOrg(currentTask.assigneeOrg?.id || null);
                  setSelectedAssignees(
                    currentTask.assigneeUsers?.[0]?.id || []
                  );
                  setSelectedTeamId(currentTask?.teams?.[0]?.id || null);
                }}
              >
                Cancel
              </Button>
              <Button
                type="primary"
                size="small"
                icon={<CheckOutlined />}
                loading={updateTaskLoading}
                onClick={onUserUpdate}
              >
                Save
              </Button>
            </div>
          </div>
        )}

        {/* Collaborators */}
        <div className="task-sheet-content__collaborators">
          <div className="task-sheet-content__card-header">
            <div className="task-sheet-content__card-title">
              Collaborators
              {currentTask.collaborators?.length > 0 && (
                <span className="task-sheet-content__count">
                  ({currentTask.collaborators.length})
                </span>
              )}
            </div>
            {level === 'PLATFORM' && (
              <Button
                type="text"
                size="small"
                onClick={onCollaboratorsUpdate}
                disabled={updateTaskLoading}
                className={`task-sheet-content__action-btn ${isCollaborator ? 'task-sheet-content__action-btn--leave' : 'task-sheet-content__action-btn--join'}`}
              >
                {isCollaborator ? (
                  <>
                    <MinusOutlined /> Leave
                  </>
                ) : (
                  <>
                    <PlusOutlined /> Join
                  </>
                )}
              </Button>
            )}
          </div>

          {currentTask.collaborators?.length > 0 ? (
            <div className="task-sheet-content__avatar-stack">
              {currentTask.collaborators.map((collaborator) => (
                <Tooltip
                  key={collaborator.id}
                  title={startCase(collaborator.name || collaborator.username)}
                >
                  <div className="task-sheet-content__avatar-item">
                    <AvatarS3
                      imgKey={collaborator.avatar?.key}
                      username={collaborator.name || collaborator.username}
                      avatarProps={{ size: 28 }}
                    />
                  </div>
                </Tooltip>
              ))}
            </div>
          ) : (
            <p className="task-sheet-content__empty-text">
              No collaborators. Join to help with this task.
            </p>
          )}
        </div>
      </div>

    </div>
  );
};

TaskDetailsSheetContent.propTypes = {
  currentTask: PropTypes.object,
  currentUser: PropTypes.shape({
    id: PropTypes.string.isRequired,
  }).isRequired,
  currentTaskId: PropTypes.string.isRequired,
  isFollowers: PropTypes.bool,
  isCurrentUserAssignee: PropTypes.bool,
  isCollaborator: PropTypes.bool,
  updateTaskLoading: PropTypes.bool,
  updateTask: PropTypes.func.isRequired,
  refetchTask: PropTypes.func.isRequired,
  onFollowChange: PropTypes.func.isRequired,
  onCollaboratorsUpdate: PropTypes.func.isRequired,
  level: PropTypes.string,
  isEditingAssignee: PropTypes.bool,
  setIsEditingAssignee: PropTypes.func,
  selectedOrg: PropTypes.string,
  setSelectedOrg: PropTypes.func,
  selectedAssignees: PropTypes.oneOfType([PropTypes.string, PropTypes.array]),
  setSelectedAssignees: PropTypes.func,
  fetchBasicOrgs: PropTypes.func,
  fetchBasicUsers: PropTypes.func,
  basicOrgsDetails: PropTypes.array,
  basicOrgsDetailsLoading: PropTypes.bool,
  basicUsersDetails: PropTypes.array,
  basicUsersDetailsLoading: PropTypes.bool,
  onUserUpdate: PropTypes.func,
  teams: PropTypes.array,
  teamsLoading: PropTypes.bool,
  selectedTeamId: PropTypes.string,
  setSelectedTeamId: PropTypes.func,
  fetchTeamsByOrg: PropTypes.func,
  isEditingDetails: PropTypes.bool,
  setIsEditingDetails: PropTypes.func,
  detailsForm: PropTypes.shape({
    urgency: PropTypes.string,
    startDate: PropTypes.object,
    dueDate: PropTypes.object,
    requestType: PropTypes.string,
    requestSubType: PropTypes.string,
    channelPartnerId: PropTypes.string,
    advertiserId: PropTypes.string,
  }),
  onDetailsFieldChange: PropTypes.func,
  onDetailsSave: PropTypes.func,
  onDetailsCancel: PropTypes.func,
  channelPartnerOrgs: PropTypes.array,
  advertiserOrgs: PropTypes.array,
  fetchChannelPartnerOrgs: PropTypes.func,
  fetchAdvertiserOrgs: PropTypes.func,
};

TaskDetailsSheetContent.defaultProps = {
  currentTask: null,
  isFollowers: false,
  isCurrentUserAssignee: false,
  isCollaborator: false,
  updateTaskLoading: false,
  level: null,
  isEditingAssignee: false,
  setIsEditingAssignee: () => {},
  selectedOrg: null,
  setSelectedOrg: () => {},
  selectedAssignees: [],
  setSelectedAssignees: () => {},
  fetchBasicOrgs: () => {},
  fetchBasicUsers: () => {},
  basicOrgsDetails: [],
  basicOrgsDetailsLoading: false,
  basicUsersDetails: [],
  basicUsersDetailsLoading: false,
  onUserUpdate: () => {},
  teams: [],
  teamsLoading: false,
  selectedTeamId: null,
  setSelectedTeamId: () => {},
  fetchTeamsByOrg: () => {},
  isEditingDetails: false,
  setIsEditingDetails: () => {},
  detailsForm: {
    urgency: null,
    startDate: null,
    dueDate: null,
    requestType: null,
    requestSubType: null,
    channelPartnerId: null,
    advertiserId: null,
  },
  onDetailsFieldChange: () => {},
  onDetailsSave: () => {},
  onDetailsCancel: () => {},
  channelPartnerOrgs: [],
  advertiserOrgs: [],
  fetchChannelPartnerOrgs: () => {},
  fetchAdvertiserOrgs: () => {},
};

export default TaskDetailsSheetContent;
